// src/components/scheduler/CalendarScheduler.tsx
import React, { useState, useEffect } from 'react';
import { useScheduler } from './SchedulerContext';
import { AvailabilityResponse, BookingRequest, BookingResponse } from './types';
import { CalendarView } from './CalendarView';
import { TimeSlotPicker } from './TimeSlotPicker';

interface CalendarSchedulerProps {
    onComplete: (appointmentId: string) => void;
}

export const CalendarScheduler: React.FC<CalendarSchedulerProps> = ({ onComplete }) => {
    const { leadData, setAppointmentData, apiBaseUrl } = useScheduler();
    const [loading, setLoading] = useState(true);
    const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [timezone, setTimezone] = useState<string>('');
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchAvailability();
        detectTimezone();
    }, []);

    const detectTimezone = () => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTimezone(tz);
    };

    const fetchAvailability = async () => {
        try {
            setLoading(true);
            console.log('Fetching from:', `${apiBaseUrl}/api/availability/slots`);
            const response = await fetch(`${apiBaseUrl}/api/availability/slots`);
            if (!response.ok) {
                console.error('Response not OK:', response.status, response.statusText);
                throw new Error('Failed to fetch availability');
            }

            const data: AvailabilityResponse = await response.json();
            console.log('Availability data:', data);
            setAvailability(data);

            // Auto-select first available date
            const dates = Object.keys(data.availability);
            if (dates.length > 0) {
                setSelectedDate(dates[0]);
            }
        } catch (err) {
            setError('Unable to load available times. Please try again.');
            console.error('Error fetching availability:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!leadData || !selectedDate || !selectedTime) return;

        setConfirming(true);
        setError(null);

        try {
            const bookingData: BookingRequest = {
                phone: leadData.phone,
                first_name: leadData.firstName,
                last_name: leadData.lastName,
                email: leadData.email,
                employee_count: leadData.employeeCount,
                average_revenue: leadData.averageRevenue,
                growth_motivation: leadData.growthMotivation,
                website_url: leadData.websiteUrl,
                date: selectedDate,
                time: selectedTime,
                timezone: timezone || undefined
            };

            const response = await fetch(`${apiBaseUrl}/api/booking/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bookingData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create booking');
            }

            const result: BookingResponse = await response.json();

            setAppointmentData({
                date: selectedDate,
                time: selectedTime,
                timezone: timezone || availability?.timezone || 'America/Chicago'
            });

            onComplete(result.appointment_id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to schedule appointment. Please try again.');
            console.error('Booking error:', err);
        } finally {
            setConfirming(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e2ba57]"></div>
            </div>
        );
    }

    if (!availability || Object.keys(availability.availability).length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-600 mb-4">No available time slots in the next few days.</p>
                <button
                    onClick={fetchAvailability}
                    className="text-[#e2ba57] hover:text-[#d1a43f] font-medium"
                >
                    Check again
                </button>
            </div>
        );
    }

    const availableDates = Object.keys(availability.availability);
    const currentDateSlots = selectedDate ? availability.availability[selectedDate]?.slots || [] : [];

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-[#212A37] mb-2">
                    Select Your Preferred Time
                </h3>
                <p className="text-gray-600">
                    All times shown in {timezone || availability.timezone}
                </p>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
                {/* Calendar */}
                <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Select Date</h4>
                    <CalendarView
                        availableDates={availableDates}
                        selectedDate={selectedDate}
                        onDateSelect={setSelectedDate}
                        availability={availability.availability}
                    />
                </div>

                {/* Time Slots */}
                <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Available Times {selectedDate && availability.availability[selectedDate] && (
                            <span className="text-gray-500 font-normal">
                                - {availability.availability[selectedDate].formattedDate}
                            </span>
                        )}
                    </h4>
                    {selectedDate && currentDateSlots.length > 0 ? (
                        <TimeSlotPicker
                            slots={currentDateSlots}
                            selectedTime={selectedTime}
                            onTimeSelect={setSelectedTime}
                        />
                    ) : (
                        <p className="text-gray-500 text-sm">Select a date to see available times</p>
                    )}
                </div>
            </div>

            {/* Confirm Button */}
            {selectedTime && (
                <div className="text-center pt-6">
                    <button
                        onClick={handleConfirm}
                        disabled={confirming}
                        className={`px-8 py-3 rounded-md font-semibold transition-all ${confirming
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-[#e2ba57] text-[#212A37] hover:bg-[#d1a43f]'
                            }`}
                    >
                        {confirming ? 'Scheduling...' : 'Confirm Appointment'}
                    </button>
                </div>
            )}
        </div>
    );
};